declare let _bcq: any;

import {
  BeaconSetupDetails,
  LogLevel,
  Params,
  PulseConfig,
  PulseData,
  UserInfo,
} from './types';
import { logLevelToInt } from './utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS Z';

class PulseLogger {
  private isDebug: boolean;
  private enabled: boolean;
  private logLevel: LogLevel;
  private context: string;
  private userInfo: UserInfo;
  private sessionId: string;
  private sessionStartTime: string;
  private sessionTimeZone: string;
  private beaconValues: BeaconSetupDetails;

  constructor(setupConfig: PulseConfig) {
    const {
      beaconConfig,
      appContext,
      logLevel = 'info',
      debugMode = false,
      enabled = true,
    } = setupConfig;
    this.isDebug = debugMode;
    this.enabled = enabled;
    this.logLevel = logLevel;
    this.context = appContext;
    this.beaconValues = beaconConfig;
    this.userInfo = {};
    this.sessionId = crypto.randomUUID();
    this.sessionStartTime = this.getTimestamp();
    this.sessionTimeZone = this.getTimeZone();
    if (this.isDebug) {
      console.info('Initializing pulse', {
        debugMode,
        appContext,
        beaconConfig,
      });
    }
    this.loadPulseBeacon();
    this.initializePulseBeacon();
  }

  private getTimestamp() {
    return dayjs.utc().format(DATE_FORMAT);
  }

  private getTimeZone() {
    return dayjs.tz.guess();
  }

  private getBeaconDetails(detail: 'bd' | 'bh' | 'url') {
    return this.beaconValues[detail];
  }

  private loadPulseBeacon() {
    if (!this.enabled) {
      if (this.isDebug) {
        console.info('Not loading Beacon script', { enabled: this.enabled });
      }
      return;
    }
    const script = document.createElement('script');
    script.src = this.getBeaconDetails('url');
    script.async = true;
    document.head.appendChild(script);
    if (this.isDebug) {
      console.info('Beacon script loaded', {
        url: this.getBeaconDetails('url'),
      });
    }
  }

  private initializePulseBeacon() {
    if (!this.enabled) {
      if (this.isDebug) {
        console.info('Not initializing Beacon script', {
          enabled: this.enabled,
        });
      }
      return;
    }
    const BD = this.getBeaconDetails('bd');
    const BH = this.getBeaconDetails('bh');

    const innerScript = `var _bcq = _bcq || [];
    _bcq.push([
      '_setOptions',
      {
        start_time: new Date().getTime(),
        bd: '${BD}',
        bh: '${BH}'
      }
    ]);`;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    const scriptContent = document.createTextNode(innerScript);
    script.appendChild(scriptContent);
    document.head.appendChild(script);
    if (this.isDebug) {
      console.info('Beacon initialization complete', { bd: BD, bh: BH });
    }
  }

  /**
   * Sets the session ID and updates the session start time.
   *
   * @param {string} sessionId - The new session ID to be set.
   * @return {void} This function does not return anything.
   */
  setSessionInfo(sessionId: string) {
    this.sessionId = sessionId;
    this.sessionStartTime = this.getTimestamp();
    this.sessionTimeZone = this.getTimeZone();
  }

  /**
   * Updates and overrides the user information with the provided values.
   *
   * @param {UserInfo} userInfo - The new user information to be set.
   * @return {void} This function does not return anything.
   */
  setUserInfo(userInfo: UserInfo) {
    this.userInfo = {
      ...this.userInfo,
      ...userInfo,
    };
  }

  /**
   * Sets the log level for the current instance.
   *
   * @param {LogLevel} logLevel - The new log level to be set.
   * @return {void} This function does not return anything.
   */
  setLogLevel(logLevel: LogLevel) {
    this.logLevel = logLevel;
  }

  /**
   * Determines if a log message of the given level should be logged based on the current log level.
   *
   * @param {LogLevel} level - The log level of the message to be logged.
   * @return {boolean} Returns true if the message should be logged, false otherwise.
   */
  private shouldLog(level: LogLevel) {
    const logLevelWeight = logLevelToInt(this.logLevel);
    const currentLevelWeight = logLevelToInt(level);
    return currentLevelWeight >= logLevelWeight;
  }

  private sendDataToPulse = (pulseData: PulseData) => {
    const timestamp = this.getTimestamp();
    const action = 'overlayView';
    const actionSubCateg = pulseData.event;
    const data = {
      ta: {
        level: pulseData.level,
        message: pulseData.message,
        params: pulseData.params,
        event: pulseData.event,
        timestamp,
        userInfo: this.userInfo,
        sessionInfo: {
          sessionId: this.sessionId,
          sessionStartTime: this.sessionStartTime,
          sessionTimeZone: this.sessionTimeZone,
        },
      },
    };

    if (this.isDebug) {
      console.info('Sending pulse event', {
        data,
        action,
        actionSubCateg,
        context: this.context,
      });
    }

    if (this.enabled) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        _bcq = _bcq || [];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        _bcq?.push(['_tagAction', this.context, action, actionSubCateg, data]);
      } catch (error) {
        // silent failure
        if (this.isDebug) {
          console.error(
            'Failed to send pulse event',
            { data, action, actionSubCateg, context: this.context },
            error,
          );
        }
      }
    }
  };

  private log(message: string, params: Params, level: LogLevel) {
    if (this.shouldLog(level)) {
      this.sendDataToPulse({
        event: 'log',
        level,
        message,
        params,
      });
    }
  }

  debug(message: string, params: Params) {
    this.log(message, params, 'debug');
  }

  info(message: string, params: Params) {
    this.log(message, params, 'info');
  }

  warn(message: string, params: Params) {
    this.log(message, params, 'warn');
  }

  error(message: string, params: Params) {
    this.log(message, params, 'error');
  }

  fatal(message: string, params: Params) {
    this.log(message, params, 'fatal');
  }

  /**
   * Logs a page view event. This function ignores logLevel and sends event to pulse
   *
   * @param {string} currentPage - The current page being viewed.
   */
  logPageView(currentPage: string) {
    this.sendDataToPulse({
      event: 'pageView',
      level: 'info',
      message: 'PageView',
      params: { currentPage },
    });
  }

  /**
   * Logs an analytics event.
   *
   * @param {string} event - The name of the analytics event.
   * @param {Params} params - Additional parameters for the analytics event.
   * @return {void} This function does not return anything.
   */
  analytics(event: string, params?: Params) {
    this.sendDataToPulse({
      event: 'analytics',
      level: 'info',
      message: event,
      params: params ?? {},
    });
  }

  /**
   * Logs a click stream event.
   *
   * @param {string} event - The name of the click stream event.
   * @param {Params} params - Additional parameters for the click stream event.
   * @return {void} This function does not return anything.
   */
  clickStream(event: string, params?: Params) {
    this.sendDataToPulse({
      event: 'clickStream',
      level: 'info',
      message: event,
      params: params ?? {},
    });
  }
}

export default PulseLogger;
